import { createFileRoute, Link } from "@tanstack/react-router";
import { BRAND } from "@/lib/behot";
import { LEGAL_OG_IMAGE, LEGAL_OG_IMAGE_ALT, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/cookie")({
  head: () =>
    buildPageMetadata({
      path: "/cookie",
      title: "Cookie Policy | BeHot.Now",
      description:
        "Which cookies and similar technologies BeHot.Now uses for sign-in, payments, referral attribution and analytics, and how to control them.",
      ogTitle: "Cookie Policy",
      keywords: [
        "cookie policy",
        "cookie consent",
        "analytics cookies",
        "tracking preferences",
        "GDPR cookies",
      ],
      ogDescription: "The cookies BeHot.Now sets and why.",
      image: LEGAL_OG_IMAGE,
      imageAlt: LEGAL_OG_IMAGE_ALT,
      twitterCard: "summary_large_image",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Cookies", path: "/cookie" },
      ],
    }),
  component: CookiePolicy,
});

const ROW_KEYS = ["auth", "referral", "payment", "preferences", "analytics"] as const;

function CookiePolicy() {
  const { t, locale } = useT();
  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-4xl font-bold">{t("legal.cookie.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("legal.cookie.updated")}</p>
      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        {t("legal.cookie.intro", { operator: BRAND.operator, domain: BRAND.domain })}
      </p>

      <div className="card-surface mt-8 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="p-4">{t("legal.cookie.table.cookie")}</th>
              <th className="p-4">{t("legal.cookie.table.category")}</th>
              <th className="p-4">{t("legal.cookie.table.purpose")}</th>
              <th className="p-4">{t("legal.cookie.table.lifetime")}</th>
            </tr>
          </thead>
          <tbody>
            {ROW_KEYS.map((key) => (
              <tr key={key} className="border-b border-border align-top last:border-0">
                <td className="p-4 font-medium">{t(`legal.cookie.row.${key}.name`)}</td>
                <td className="p-4 text-muted-foreground">{t(`legal.cookie.row.${key}.type`)}</td>
                <td className="p-4 text-muted-foreground">{t(`legal.cookie.row.${key}.purpose`)}</td>
                <td className="p-4 text-muted-foreground">{t(`legal.cookie.row.${key}.life`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <section className="mt-8">
        <h2 className="font-display text-xl font-bold">{t("legal.cookie.thirdParties.title")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {t("legal.cookie.thirdParties.body")}
        </p>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-xl font-bold">{t("legal.cookie.controlling.title")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {t("legal.cookie.controlling.body")}
        </p>
      </section>

      <section className="mt-8">
        <h2 className="font-display text-xl font-bold">{t("legal.cookie.dnt.title")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t("legal.cookie.dnt.body")}</p>
      </section>

      <p className="mt-10 text-sm text-muted-foreground">
        {t("legal.cookie.moreDetail")}{" "}
        <Link to="/privacy" className="text-primary hover:underline">
          {t("legal.common.privacyPolicy")}
        </Link>{" "}
        {t("legal.common.and")}{" "}
        <Link to="/terms" className="text-primary hover:underline">
          {t("legal.common.termsOfService")}
        </Link>
        .
      </p>

      {locale !== "en" ? (
        <p className="mt-6 text-xs text-muted-foreground">{t("legal.common.englishPrevails")}</p>
      ) : null}
    </div>
  );
}
